"use client";

import { Icon } from "@iconify/react";
import { useTranslation } from "react-i18next";

// Generate mock contribution data for visualization
// In a real implementation, you'd fetch this from GitHub's GraphQL API
function generateMockContributions(): number[] {
  const weeks = 52;
  const daysPerWeek = 7;
  const contributions: number[] = [];

  for (let i = 0; i < weeks * daysPerWeek; i++) {
    // Random contribution count with some realistic patterns
    const rand = Math.random();
    if (rand < 0.4) {
      contributions.push(0);
    } else if (rand < 0.7) {
      contributions.push(Math.floor(Math.random() * 3) + 1);
    } else if (rand < 0.9) {
      contributions.push(Math.floor(Math.random() * 6) + 3);
    } else {
      contributions.push(Math.floor(Math.random() * 10) + 8);
    }
  }

  return contributions;
}

const getContributionColor = (count: number): string => {
  if (count === 0) return "bg-muted";
  if (count <= 3) return "bg-primary/30";
  if (count <= 6) return "bg-primary/50";
  if (count <= 9) return "bg-primary/70";
  return "bg-primary";
};

const getContributionLevel = (count: number, t: any): string => {
  if (count === 0) return t("Stats.contribution.noContributions");
  if (count <= 3) return t("Stats.contribution.levelLow");
  if (count <= 6) return t("Stats.contribution.levelMedium");
  if (count <= 9) return t("Stats.contribution.levelHigh");
  return t("Stats.contribution.levelVeryHigh");
};

const formatDate = (date: Date, locale: string): string => {
  return date.toLocaleDateString(locale, {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

export function ContributionGraph() {
  const { t, i18n } = useTranslation();
  const locale = i18n.language || "es";
  const contributions = generateMockContributions();
  const weeks = 52;
  const daysPerWeek = 7;

  // Calculate total contributions
  const totalContributions = contributions.reduce((sum, count) => sum + count, 0);

  // Calculate current streak (simplified)
  let currentStreak = 0;
  for (let i = contributions.length - 1; i >= 0; i--) {
    if (contributions[i] > 0) {
      currentStreak++;
    } else {
      break;
    }
  }

  // Get month labels
  const months: string[] = [];
  const now = new Date();
  for (let i = 11; i >= 0; i--) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    months.push(d.toLocaleDateString(locale, { month: "short" }));
  }

  return (
    <div className="rounded-2xl border border-border/50 bg-card p-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold flex items-center gap-2">
          <Icon icon="solar:calendar-mark-linear" className="w-5 h-5 text-primary" />
          {t("Stats.contribution.title")}
        </h3>
        <div className="text-sm text-muted-foreground">
          <span className="font-semibold text-foreground">{totalContributions}</span> {t("Stats.contribution.countLastYear", { count: totalContributions })}
        </div>
      </div>

      {/* Stats summary */}
      <div className="grid grid-cols-2 gap-4 mb-6">
        <div className="p-3 rounded-xl bg-accent/50 border border-border/50">
          <div className="text-2xl font-bold text-primary">{currentStreak}</div>
          <div className="text-xs text-muted-foreground">{t("Stats.statistics.currentStreak")}</div>
        </div>
        <div className="p-3 rounded-xl bg-accent/50 border border-border/50">
          <div className="text-2xl font-bold text-secondary">
            {Math.max(...contributions)}
          </div>
          <div className="text-xs text-muted-foreground">{t("Stats.statistics.bestDay")}</div>
        </div>
      </div>

      {/* Contribution grid */}
      <div className="overflow-x-auto pb-2">
        <div className="min-w-[750px]">
          {/* Month labels */}
          <div className="flex gap-1 mb-1">
            <div className="w-8" /> {/* Spacer for day labels */}
            {months.map((month, i) => (
              <div key={i} className="flex-1 text-[10px] text-muted-foreground">
                {month}
              </div>
            ))}
          </div>

          {/* Grid */}
          <div className="flex gap-1">
            {/* Day labels */}
            <div className="flex flex-col gap-1 w-8">
              {[t("Stats.contribution.days.mon"), t("Stats.contribution.days.wed"), t("Stats.contribution.days.fri")].map((day) => (
                <div key={day} className="text-[10px] text-muted-foreground h-3 leading-3">
                  {day}
                </div>
              ))}
            </div>

            {/* Weeks */}
            <div className="flex gap-1">
              {Array.from({ length: weeks }).map((_, weekIndex) => (
                <div key={weekIndex} className="flex flex-col gap-1">
                  {Array.from({ length: daysPerWeek }).map((_, dayIndex) => {
                    const index = weekIndex * daysPerWeek + dayIndex;
                    const count = contributions[index] || 0;
                    const date = new Date();
                    date.setDate(date.getDate() - (contributions.length - index));

                    return (
                      <div
                        key={dayIndex}
                        className={`w-3 h-3 rounded-sm ${getContributionColor(count)} transition-colors hover:ring-2 hover:ring-primary/50 cursor-pointer`}
                        title={t("Stats.contribution.onDate", { count, date: formatDate(date, locale) })}
                      />
                    );
                  })}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Legend */}
      <div className="flex items-center justify-end gap-2 mt-4 text-xs text-muted-foreground">
        <span>{t("Stats.contribution.less")}</span>
        <div className="flex gap-1">
          {[0, 2, 5, 8, 12].map((count) => (
            <div
              key={count}
              className={`w-3 h-3 rounded-sm ${getContributionColor(count)}`}
              title={getContributionLevel(count, t)}
            />
          ))}
        </div>
        <span>{t("Stats.contribution.more")}</span>
      </div>
    </div>
  );
}
